import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useCallback, useEffect, useState } from 'react';

import { sendSafeError } from '#src/lib/error.ts';
import { localStorage } from '#src/lib/storage/localStorage.ts';
import { sessionStorage } from '#src/lib/storage/sessionStorage.ts';

import type {
  SetDeviceTokenMutation,
  SetDeviceTokenMutationVariables,
} from './__generated__/useTwaPushNotifications.graphql.ts';
import { useMutation } from './graphql/useMutation.ts';
import { pushSettingsReturnAction } from './pushSettingsReturnAction.ts';
import { useIsMobileApp } from './useIsMobileApp.ts';

// keep in sync with apps/sorare/public/firebase-messaging-sw.js
const firebaseConfig = {
  apiKey: 'AIzaSyBgMA8cOhgZZYE_QiyrA_1U8MiCYW7bpxk',
  appId: '1:354638266918:android:6d2e038e6046689391dce7',
  messagingSenderId: '354638266918',
  projectId: 'sorare-inc',
};

// The TWA wraps the website inside the Android app, so the backend delivers to
// its FCM tokens through the existing Android device platform.
const TWA_DEVICE_PLATFORM = 'com.sorare.app.android';

// deep-link into the native Android app's notification settings activity
const TWA_NOTIFICATION_SETTINGS_INTENT =
  'intent://notification-settings#Intent;scheme=sorare;package=com.sorare.app.android;end';

const SERVICE_WORKER_PATH = '/firebase-messaging-sw.js';

const VAPID_KEY =
  'BBGrF4Silfrld3p1N5TXm9LRjdf5UD4HNNVKnWNVQVkKbUlGQyY6T8mSSLDzLS9Z99w52zJWgfQke9_LSkJ7sus';

const registerServiceWorkerAndgetFcmToken = async () => {
  // dynamic imports to keep the firebase SDK out of the main bundle;
  // the deprecated getToken stays in use because the backend setDeviceToken
  // pipeline expects an FCM registration token, not an Installation ID
  // oxlint-disable-next-line typescript/no-deprecated
  const [{ initializeApp, getApp, getApps }, { getMessaging, getToken }] =
    await Promise.all([import('firebase/app'), import('firebase/messaging')]);

  const firebaseApp = getApps().length
    ? getApp()
    : initializeApp(firebaseConfig);
  const serviceWorkerRegistration =
    await navigator.serviceWorker.register(SERVICE_WORKER_PATH);

  // oxlint-disable-next-line typescript/no-deprecated
  return getToken(getMessaging(firebaseApp), {
    vapidKey: VAPID_KEY,
    serviceWorkerRegistration,
  });
};

const SET_DEVICE_TOKEN_MUTATION = gql`
  mutation SetDeviceTokenMutation($input: setDeviceTokenInput!) {
    setDeviceToken(input: $input) {
      errors {
        message
      }
    }
  }
` as TypedDocumentNode<SetDeviceTokenMutation, SetDeviceTokenMutationVariables>;

const isPushSupported = () =>
  typeof Notification !== 'undefined' &&
  'serviceWorker' in navigator &&
  'PushManager' in window;

// also used on logout, where it must run while the user is still authenticated
export const useRevokeTwaDeviceToken = () => {
  const [setDeviceToken] = useMutation(SET_DEVICE_TOKEN_MUTATION, {
    sendErrors: true,
  });

  return useCallback(async () => {
    const deviceToken = localStorage.getItem('TWA_FCM_DEVICE_TOKEN');
    if (!deviceToken) {
      return true;
    }

    const { success } = await setDeviceToken({
      variables: {
        input: {
          deviceToken,
          devicePlatform: TWA_DEVICE_PLATFORM,
          disable: true,
        },
      },
    });

    if (success) {
      // forget the token so a later enable re-sends it to the backend
      localStorage.removeItem('TWA_FCM_DEVICE_TOKEN');
    }

    return success;
  }, [setDeviceToken]);
};

// an existing stored token is what marks notifications as enabled on this device
const isEnabledOnDevice = () =>
  isPushSupported() &&
  Notification.permission === 'granted' &&
  !!localStorage.getItem('TWA_FCM_DEVICE_TOKEN');

// the hook can be mounted several times; sync the token at most once per page load
let syncedOnLaunch = false;

export const useTwaPushNotifications = () => {
  const { isTwa } = useIsMobileApp();
  const [setDeviceToken] = useMutation(SET_DEVICE_TOKEN_MUTATION, {
    showErrorsWithSnackNotification: true,
  });
  const revokeDeviceToken = useRevokeTwaDeviceToken();

  const canEnable = isTwa && isPushSupported();
  const [isEnabled, setIsEnabled] = useState(
    () => isTwa && isEnabledOnDevice()
  );
  const [pendingSettings, setPendingSettings] = useState(
    () => !!sessionStorage.getItem('twaPushPendingSettings')
  );

  const syncToken = useCallback(async () => {
    const token = await registerServiceWorkerAndgetFcmToken();
    if (!token) {
      return false;
    }
    if (token === localStorage.getItem('TWA_FCM_DEVICE_TOKEN')) {
      return true;
    }

    const { success } = await setDeviceToken({
      variables: {
        input: { deviceToken: token, devicePlatform: TWA_DEVICE_PLATFORM },
      },
    });

    if (success) {
      localStorage.setItem('TWA_FCM_DEVICE_TOKEN', token);
    }

    return success;
  }, [setDeviceToken]);

  const enable = useCallback(async () => {
    if (!canEnable) {
      return;
    }

    try {
      const permission = await Notification.requestPermission();

      if (permission === 'denied') {
        sessionStorage.setItem('twaPushPendingSettings', 'enable');
        setPendingSettings(true);
        // oxlint-disable-next-line no-restricted-properties
        window.location.href = TWA_NOTIFICATION_SETTINGS_INTENT;
        return;
      }
      if (permission !== 'granted') {
        return;
      }
      if (await syncToken()) {
        setIsEnabled(true);
      }
    } catch (error) {
      sendSafeError(error);
    }
  }, [canEnable, syncToken]);

  const disable = useCallback(() => {
    if (!canEnable) {
      return;
    }
    sessionStorage.setItem('twaPushPendingSettings', 'disable');
    setPendingSettings(true);
    // oxlint-disable-next-line no-restricted-properties
    window.location.href = TWA_NOTIFICATION_SETTINGS_INTENT;
  }, [canEnable]);

  useEffect(() => {
    if (!canEnable || !pendingSettings) {
      return undefined;
    }

    let inFlight = false;

    const resume = () => {
      if (document.visibilityState !== 'visible') {
        return;
      }

      const action = pushSettingsReturnAction({
        direction: sessionStorage.getItem('twaPushPendingSettings'),
        permissionGranted: Notification.permission === 'granted',
      });
      if (action === 'wait') {
        return;
      }

      if (inFlight) {
        return;
      }
      inFlight = true;

      const settled =
        action === 'enable'
          ? syncToken().then(success => {
              if (success) {
                setIsEnabled(true);
              }
            })
          : revokeDeviceToken().then(revoked => {
              if (revoked) {
                setIsEnabled(false);
              }
            });

      settled.catch(sendSafeError).finally(() => {
        sessionStorage.removeItem('twaPushPendingSettings');
        setPendingSettings(false);
        inFlight = false;
      });
    };

    resume();
    document.addEventListener('visibilitychange', resume);
    return () => document.removeEventListener('visibilitychange', resume);
  }, [canEnable, pendingSettings, syncToken, revokeDeviceToken]);

  useEffect(() => {
    if (!canEnable || syncedOnLaunch || Notification.permission !== 'granted') {
      return;
    }
    syncedOnLaunch = true;

    syncToken()
      .then(success => {
        if (success) {
          setIsEnabled(true);
        }
      })
      .catch(sendSafeError);
  }, [canEnable, syncToken]);

  return { canEnable, isEnabled, enable, disable };
};
